/**
 * Salary Timer — Core Type Definitions
 * 单一来源真理:所有跨模块类型在此定义。
 *
 * v1.3 扩展:
 *   - WorkSegment(多段工时)
 *   - DayOverrideEntry 增加 segments / nightShift
 *   - Config 增加 salaryMode / manualHourlyRate / manualDailyRate / segments / lunch*
 *   - SlackingSession / SlackingSessions(摸鱼会话)
 *   - SalaryMode 联合类型
 */

// ── User Config ─────────────────────────────────────────────

/**
 * 薪资模式
 * - monthly:按月结(现有模型,月薪 + 固定上下班)
 * - hourly:按时结(兼职,manualHourlyRate 决定时薪)
 * - daily:按日结(兼职/日薪,manualDailyRate 决定日薪)
 */
export type SalaryMode = 'monthly' | 'hourly' | 'daily';

/** 一段工时(不区分跨天,end < start 时自动识别为跨天) */
export interface WorkSegment {
  /** "HH:MM",例如 "09:00" */
  start: string;
  /** "HH:MM",允许 < start 表示跨天(如 22:00 - 06:00) */
  end: string;
}

/**
 * 多段工时模板(v1.3.1 bug fix 引入)
 *
 * 设计:
 *   - 用户在「设置」页配置 N 个模板(每个模板 = 命名 + 段列表)
 *   - 在「日历」页点击日期 → 选择「自定义」→ 勾选需要的模板 → 合并写入当日 segments
 *   - 解耦"全局默认工时"与"日历页可选模板"
 *
 * 模板示例:
 *   { id: 'tpl-1', label: '早班', segments: [{ start: '06:00', end: '14:00' }] }
 *   { id: 'tpl-2', label: '夜班', segments: [{ start: '22:00', end: '06:00' }] }  // 跨天
 */
export interface SegmentTemplate {
  id: string;
  label: string;          // 用户命名,如 "早班"/"晚班"/"周末加班"
  segments: WorkSegment[];
  /** v1.3.5 单段模板；segments 保留用于兼容旧数据 */
  segment?: WorkSegment;
}

/**
 * v1.3.5 工作日模板（多模板分组标记系统）
 * 
 * 用于日历页标记工作日的模板系统。
 * 区别于 SegmentTemplate（设置页工时模板），WorkTemplate 用于日历页的彩色标记点。
 * 
 * 特性：
 *   - 动态数量（无上限）
 *   - 颜色自动循环分配（TEMPLATE_COLORS 4 色池）
 *   - 单段工时（不支持多段，简化模型）
 *   - 日历页顶部横向选择器展示
 * 
 * 标记规则：
 *   - 日期被任意模板标记 → 自动判定为工作日
 *   - 同一天可叠加多个模板（小点并排显示）
 *   - 工时段自动合并去重叠（unionSegments）
 *   - 时间冲突校验（重叠时禁止添加）
 * 
 * 示例：
 *   { id: 'tpl-uuid1', name: '常规班', color: '#4ADE80', workSegment: { start: '09:00', end: '18:00' } }
 *   { id: 'tpl-uuid2', name: '夜班',   color: '#FBBF24', workSegment: { start: '22:00', end: '06:00' } }
 */
export interface WorkTemplate {
  /** 唯一标识，格式 'tpl-<uuid>' */
  id: string;
  /** 用户自定义名称，默认 '模板1' / '模板2' ... */
  name: string;
  /** CSS hex 颜色，自动循环分配或手动修改 */
  color: string;
  /** 单段工时（不支持多段） */
  workSegment: WorkSegment;
}

export interface CustomRestSchedule {
  /** YYYY-MM-DD -> template ids（v1.3.5 起不再包含 'inherit',但保留字符串兼容旧数据） */
  workDays: Record<string, string[]>;
  updatedAt: number;
}

export interface Config {
  /** 月薪总额(monthly 模式使用) */
  monthlySalary: number;
  /** 上班时间 "HH:MM"(单段 fallback) */
  startTime: string;
  /** 下班时间 "HH:MM"(单段 fallback) */
  endTime: string;
  /** 咖啡默认单价(¥) */
  coffeePrice: number;
  /** 休息模式:0=无休,1=单休,2=双休(monthly 模式) */
  restMode: 0 | 1 | 2 | 'custom';
  /** 主题 ID */
  theme: 'paper' | 'obsidian' | 'gold';
  /**
   * 数据完整链锚点(YYYY-MM-DD)。
   *
   * 语义(v2.5-patch19 T-540 起):
   *   - **初始值**:用户首次启用 App 时 = 今天;`onRehydrateStorage` 只在
   *     persisted 为空时写入,后续 setConfig 即使被调用也**不会**回退覆盖
   *     已有值
   *   - **滚动向前**:每次 SalaryLinkageSync tick 检查 [recordedFromDate, todayKey)
   *     工作日缺失的 record(time mode + account 联动),全部补齐后
   *     recordedFromDate := todayKey,下次 tick 走早返回
   *   - **TASK-053 立场保留**:anchor 之前(用户启用 App 之前)的工作日
   *     永远不会被 backfill 主动补 record
   *   - **作用**:用户启用后即使中间几天没打开 App,backfill 窗口会随用户
   *     每日使用滚动到 today,正常场景下永远只补「最近一段 gap」,不会因
   *     一次大跨度 gap 触发整段历史回灌
   */
  recordedFromDate: string;

  // ── v1.3 新增字段(老数据迁移时补默认值) ──
  /** 薪资模式 */
  salaryMode: SalaryMode;
  /** hourly 模式手动时薪 */
  manualHourlyRate: number;
  /** daily 模式手动日薪 */
  manualDailyRate: number;
  /**
   * 多段工时。null = 使用 startTime/endTime 单段 fallback
   * (v1.3.1 后:仅作为兜底;用户配置主要走 segmentTemplates)
   */
  segments: WorkSegment[] | null;
  /**
   * 多段工时模板库(v1.3.1 新增)
   *
   * 日历页"自定义"模式下,从这里勾选需要的模板。
   * 默认含 1 个模板(09:00-18:00),用户可自由增删。
   */
  segmentTemplates: SegmentTemplate[];
  /** 是否扣除午休 */
  lunchEnabled: boolean;
  /** 午休开始时间 "HH:MM" */
  lunchStart: string;
  /** 午休时长(分钟) */
  lunchMinutes: number;
  /** v1.3.5 自定义排班 */
  customRestSchedule?: CustomRestSchedule | null;
  /**
   * v1.3.5 工作日模板列表（多模板分组标记系统）
   * 
   * 动态数量，无上限。颜色自动循环分配。
   * 默认初始化 1 个模板：{ id: 'tpl-default', name: '常规班', color: '#4ADE80', workSegment: { start: '09:00', end: '18:00' } }
   */
  workTemplates?: WorkTemplate[];
  /**
   * v2.5 TASK-046 T-501-3：time → accounting 联动开关。
   * 开启时,time 模式日历页「当日已赚」作为 income record 同步写入「工资池」。
   * 关闭时,仅保留已存在的联动记录;后续新增 / 更新停止。
   * v2.5-patch4 N-481：默认改 false（用户取消联动展示与写入）。
   */
  salaryLinkageEnabled?: boolean;
  /**
   * v3.0 TASK-057：钱包模式开关。
   * - `false`(默认):关闭钱包。UI 不展示账户(账户选择器 / 拖拽归入 / 账户标签 / MinePage AccountRow 全部隐藏);
   *   MinePage 顶部改为「总支出」(累计支出);记录 `accountId` 字段保留以便回滚 / 切回开启。
   * - `true`:开启钱包。沿用 v2.4 行为 + 新增每账户详情页(AccountDetailPage)。
   * 切换实时生效,不 reload。
   */
  walletEnabled?: boolean;
  /**
   * v3.0 TASK-060：计时模式隐藏开关（记账纯净模式）。
   * - `false`(默认):计时/记账两模式均可见,底部 nav 可切换。
   * - `true`:隐藏计时模式入口(底部 nav 仅显示记账 tabs),应用启动直接进 /accounting。
   * 后台功能(薪资关联同步/定期账同步)继续运行,计时路由不可访问。
   * 切换实时生效,不 reload。
   */
  hideTimerMode?: boolean;
}

// ── Day Overrides(手动调休 / 加班 / 请假) ───────────────────
/**
 * 工作日类型
 * - work: 正常工作日,默认倍率 1
 * - paid_overtime: 有偿加班,默认倍率 1.5,可手动改
 * - leave: 请假,倍率 0(不计入日均,但从已赚中扣除)
 * - rest: 休息日,倍率 0
 * - freelance: 自由/兼职日(hourly/daily 模式专用,monthly 模式无意义)
 */
export type DayType = 'work' | 'paid_overtime' | 'leave' | 'rest' | 'freelance';

/** 不同类型的默认倍率 */
export const DEFAULT_MULTIPLIER: Record<DayType, number> = {
  work: 1,
  paid_overtime: 1.5,
  leave: 0,
  rest: 0,
  freelance: 1,
};

/**
 * 单日 override entry
 */
export interface DayOverrideEntry {
  type: DayType;
  /** 倍率(默认 1 / 1.5 / 0) */
  multiplier: number;
  // ── v1.3 新增(均可选,不填表示继承全局) ──
  /** null = 用全局 config.segments(或 startTime/endTime 单段) */
  segments: WorkSegment[] | null;
  /** 启用夜班加权(22:00–06:00 段 × 0.5 计入净工时) */
  nightShift: boolean;
  /** v1.3.5:由已赚批量生成写入的标记与金额快照 */
  earnedGenerated?: boolean;
  earnedAmount?: number | null;
  /** v1.3.5:生成记录时的净工时快照（分钟），修改配置不影响历史已生成的日期 */
  earnedNetMinutes?: number | null;
  /**
   * v1.3.5:该日期的模板标记列表（多模板分组标记系统）
   * 
   * 存储标记该日期的 WorkTemplate id 列表。
   * 规则：
   *   - 日期被任意模板标记 → 自动判定为工作日
   *   - 优先级：templateMarks > DaySheet override > 全局 restMode
   *   - 同一天可叠加多个模板标记（小点并排显示）
   *   - 工时段自动合并（unionSegments 去重叠）
   * 
   * 示例：['tpl-uuid1', 'tpl-uuid2'] 表示该日期同时被两个模板标记
   */
  templateMarks?: string[];
  // ── v1.3.2 新增(freelance 类型专用,其他类型忽略) ──
  /**
   * freelance 日临时日薪(¥)。type='freelance' 且 freelancer 选了「按日薪」时使用。
   * 优先级:override.freelanceDaily > config.manualDailyRate。
   * 非 freelance 类型:忽略。
   */
  freelanceDaily?: number | null;
  /**
   * freelance 日临时时薪(¥)。type='freelance' 且 freelancer 选了「按时薪」时使用。
   * 计算:feeHourly × segmentsHours × multiplier。
   * 优先级:override.freelanceHourly > config.manualHourlyRate。
   * 非 freelance 类型:忽略。
   */
  freelanceHourly?: number | null;
}

/**
 * DayOverrides: key=YYYY-MM-DD, value=DayOverrideEntry
 * 为保持 v1 兼容,运行时类型是 DayOverrideEntry;兼容老数据时
 * 由 compute/store 入口处转换。
 */
export type DayOverrides = Record<string /* YYYY-MM-DD */, DayOverrideEntry>;

/**
 * 旧版 override 值(只用于兼容过渡)
 * @deprecated
 */
export type DayOverrideValue = 'work' | 'rest';

// ── Holiday Map ─────────────────────────────────────────────
export type HolidayMap = Record<string /* YYYY-MM-DD */, string /* 节日名 */>;

// ── Convert Items ───────────────────────────────────────────
export interface Item {
  id: string;
  name: string;
  price: number;
  icon: string;
  order: number;
}

// ── Monthly Record / Snapshot ───────────────────────────────
/**
 * 月度薪资快照 —— 用户手动点击"生成"时固化。
 * 历史月份用此快照,不受 config 后续变化影响。
 *
 * key 格式 "YYYY-MM"(如 "2026-08")
 */
export interface MonthlySnapshot {
  /** YYYY-MM(如 "2026-08") */
  key: string;
  /** 该月月薪 */
  salary: number;
  /** 该月工作日数(根据生成时 overrides 计算) */
  workDays: number;
  /** 该月日均(根据生成时 overrides 计算) */
  dailyRate: number;
  /** 该月总工作单位(每个工作日 × 倍率累加) */
  totalUnits: number;
  /** 快照生成时间(ISO) */
  generatedAt: string;
}

/**
 * 快照表:key=YYYY-MM, value=MonthlySnapshot
 */
export type MonthlySnapshots = Record<string, MonthlySnapshot>;

// ── Timer Day State ─────────────────────────────────────────
export type DayStateMode = 'rest' | 'done' | 'active';

export interface DayStateActive {
  mode: 'active';
  display: string;       // "HH:MM:SS"
  label: string;         // "距离今天下班"
  status: string;        // "工作计价中"
}

export interface DayStateDone {
  mode: 'done';
  display: string;       // "HH:MM:SS"
  label: string;         // "今日完成"
  status: string;        // "今日收工"
  totalSeconds: number;
}

export interface DayStateRest {
  mode: 'rest';
}

export type DayState = DayStateActive | DayStateDone | DayStateRest;

// ── Time Records(时间记录,v1.3.3 重命名自 SlackingSessions) ────────────

/**
 * 时间记录标签(v1.3.3 重命名自 SlackingLabel)
 * - slack:摸鱼(开小差)
 * - overtime:加班
 * - parttime:兼职(v1.3.5 新增)
 * - other:其他(customLabel 必填)
 *
 * 设计变化:v1.3.0-1.3.2 的 toilet/meal 已合并到 other(customLabel 区分)
 *   保留 SlackingLabel 别名兼容旧数据。
 */
export type TimeRecordLabel = 'slack' | 'overtime' | 'parttime' | 'other';

/** @deprecated 自 v1.3.3 起改用 TimeRecordLabel(toilet/meal 已合并到 other) */
export type SlackingLabel = TimeRecordLabel;

export interface TimeRecord {
  id: string;
  /** 归属日 YYYY-MM-DD(用于聚合到日) */
  dateKey: string;
  /** 时间记录标签 */
  label: TimeRecordLabel;
  /** label='other' 时的自定义名称 */
  customLabel?: string;
  /** 开始时间戳(毫秒) */
  startTs: number;
  /** 结束时间戳(毫秒);null = 进行中 */
  endTs: number | null;
  /**
   * 夜班自动标记(v1.3.3 新增)
   * true 表示该时段跨入 22:00–06:00 窗口,后续可计入 nightBonus。
   * 规则:startTs 或 endTs 落在 [22:00, 06:00) 窗口即标 true。
   */
  nightShift: boolean;
  /**
   * v1.3.5:兼职类型专用自定义收入金额(¥)
   * 
   * 仅当 label='parttime' 时使用，可选填。
   * 用于记录该兼职时段的收入金额（类似加班可自定义倍率）。
   * 
   * 计算逻辑：
   *   - 兼职时长从净工时扣除（不计入正常工作效率统计）
   *   - 兼职时长计入加班统计展示（Fish 页「加班补偿」卡片合并显示）
   *   - 兼职收入单独累计（不影响时薪计算）
   */
  parttimeEarned?: number | null;
}

/** @deprecated 自 v1.3.3 起改用 TimeRecord */
export type SlackingSession = TimeRecord;

/** 时间记录表:key=YYYY-MM-DD, value=TimeRecord[] */
export type TimeSessions = Record<string /* YYYY-MM-DD */, TimeRecord[]>;

/** @deprecated 自 v1.3.3 起改用 TimeSessions */
export type SlackingSessions = TimeSessions;

// ── Net Hours Breakdown(v1.3 新增) ─────────────────────────

/**
 * 净工时推导明细,供 UI 展示
 *
 * v1.3.4-patch2 扩展:4 张 dashboard 卡片所需的"已发生"实时累计字段。
 * - `*Minutes` 系列:**全天总数**(UI 仅用于"已收工"场景对照/详情页 footer)
 * - `*Elapsed` 系列:**已发生**(实时累加,dashboard 2×2 主用)
 *
 * 关系:
 *   grossElapsed   = min(elapsedWorkedMin, grossMinutes)         // ≤ grossMinutes
 *   lunchElapsed   = 已发生午休(lunch 时段 ∩ 已工作窗口)
 *   slackingElapsed = 已发生摸鱼(含进行中 session,endTs===null 按 now 算)
 *   overtimeElapsed = 用户 overtime session 累计(含进行中 dayMin + nightMin)
 */
export interface NetHoursBreakdown {
  /** 总工时(分钟,跨天段 union 去重叠) */
  grossMinutes: number;
  /** 午休扣除(分钟) */
  lunchMinutes: number;
  /** 摸鱼扣除(分钟,union 去重叠) */
  slackingMinutes: number;
  /** 摸鱼∪午休(去重叠) */
  slackUnionLunch: number;
  /** 加班加成(分钟):加班日 gross × (multiplier - 1) */
  overtimeBonus: number;
  /** 夜班补偿(分钟):nightShift 时 nightMinutes × 0.5 */
  nightBonus: number;
  /** 当日是否启用夜班加权 */
  nightShiftFlag: boolean;
  /** 净工时(分钟)= gross - slackUnionLunch + overtimeBonus + nightBonus */
  netMinutes: number;

  // ── v1.3.4-patch2 新增:实时累计字段 ──
  /** 已工作分钟数(已含午休时段,封顶 grossMinutes) */
  grossElapsed: number;
  /** 已发生午休分钟数(0..lunchMinutes) */
  lunchElapsed: number;
  /** 已发生摸鱼分钟数(含进行中 session,按 now 实时累加) */
  slackingElapsed: number;
  /** 用户 overtime session 累计分钟数(含进行中 dayMin + nightMin) */
  overtimeElapsed: number;
}

// ── v2.0 Accounting ─────────────────────────────────────────

/**
 * 账户类型
 * - alipay: 支付宝
 * - wechat: 微信
 * - card: 银行卡
 * - cash: 现金
 */
export type AccountType = 'alipay' | 'wechat' | 'card' | 'cash';

/**
 * 账户
 */
export interface Account {
  id: string;
  name: string;
  type: AccountType;
  balance: number;
  color: string;
  order: number;
  createdAt: number;
}

/**
 * 记账记录类型
 * - income: 收入
 * - expense: 支出
 */
export type RecordType = 'income' | 'expense';

/**
 * 记账记录
 */
export interface AccountRecord {
  id: string;
  dateKey: string;        // YYYY-MM-DD
  amount: number;         // 正数=收入，负数=支出
  type: RecordType;
  categoryId: string;
  note?: string;
  accountId: string;
  createdAt: number;
  updatedAt: number;
  // 池关联
  poolId?: string;
  poolDirection?: 'in' | 'out';
  /**
   * v2.3 语义：
   * - 'virtual'：旧预生成虚拟记录（已废弃生成，仅兼容存量数据）
   * - 'claimed'：均摊池认领的付款记录（预付性质，不计入消费统计）
   * - 'confirmed'：存池型存入/取出记录（正常计入统计）
   */
  poolStatus?: 'virtual' | 'claimed' | 'confirmed';
  // v2.4 T-410：池周期快照 — 均摊起止日期与均摊总额（池删除后仍可溯源）
  poolCycleStart?: string;   // YYYY-MM-DD
  poolCycleEnd?: string;     // YYYY-MM-DD
  poolCycleTotal?: number;   // 该周期均摊总额
  // v2.4 T-410：该周期真实入账/支付的时间与金额（认领满额时回写，虚拟→实际）
  poolSettledAt?: number;
  poolSettledAmount?: number;
  // v2.4 T-410：池名称快照（池自动移除后记录仍能显示关联的是哪个池）
  poolName?: string;
  // 存钱目标关联（v2.4 TASK-040）：记录金额（带符号）自动累计到目标进度
  goalId?: string;
  // 分配状态
  assignedFolderId?: string;
  // 分类状态
  isUncategorized?: boolean;  // true=未分配分类
  // ── v2.5 TASK-046：联动来源标记
  // 'salary-time-mode' 表示「time 模式日历页当天已赚」联动过来；
  // 编辑 / 删除会由联动 hook 重新插入。手动记账的记录留空 / undefined。
  linkageSource?: 'salary-time-mode';
  // ── v3.0 TASK-056：附图（IndexedDB 存 Blob，localStorage 不放图片）
  imageId?: string;
  // ── v3.0 TASK-059：一程关联
  journeyId?: string;
}

/**
 * 分类（收入/支出各一套）
 */
export interface Category {
  id: string;
  name: string;
  icon: string;           // emoji
  color: string;
  type: RecordType;
  parentId?: string;
  order: number;
}

/**
 * 分类文件夹（用于首页分类卡片展示）
 */
export interface Folder {
  id: string;
  categoryId: string;
  name: string;
  icon: string;
  color: string;
  order: number;
}

// ── Pool ────────────────────────────────────────────────────

/**
 * 池类型
 * - equalize: 均摊型（长期循环，日均虚拟记录）
 * - deposit: 存池型（押金模式，不关联天数）
 */
export type PoolType = 'equalize' | 'deposit';

/**
 * 池配置
 */
export interface PoolConfig {
  id: string;
  name: string;
  type: PoolType;
  amount: number;          // 每周期总金额
  cycleMonths: number;     // 周期月数（均摊型按月模式）
  dayRange?: { start: number; end: number };  // 按月模式：每月几号到几号
  /** v2.3:按日模式：完整日期范围（YYYY-MM-DD），可跨月 */
  dateRange?: { start: string; end: string };
  targetAccountId?: string; // 目标账户（存池型）
  categoryId?: string;     // v2.3:均摊消费记录挂载的支出分类
  /** v2.3:周期模式。daily=按日（日历选日期范围）；monthly=按月（每月一周期，几号到几号） */
  cycleMode?: 'daily' | 'monthly';
  /** v2.3:用户自填日均金额（可选；不填则由总额/天数推导） */
  dailyAmount?: number;
  /** v2.4:资金方向。expense=支出池（默认，兼容存量）；income=收入池（逐日生成收入记录） */
  direction?: 'income' | 'expense';
  /**
   * v2.5 T-416：存池型结算方式。
   * prepay=押金先付（实际已扣，虚拟「待退」计入绿色资产，默认，兼容存量）；
   * postpay=先用后付（未付部分红色待付，与未分类同框）。
   */
  settleMode?: 'prepay' | 'postpay';
  /**
   * v2.5 TASK-046 T-505：「不做均摊」特殊池。
   * 仅对 income equalize 池有意义 —— 跳过 daily virtual record 生成,
   * calcVirtualAssets 也跳过该池的虚拟记录,只追踪 confirmed/claimed records。
   * 用于「工资」联动池:联动 record 本身即为"已赚",不再被日均均摊二次计数。
   */
  noDailyVirtual?: boolean;
  createdAt: number;
}

/**
 * 池周期状态
 * - generating: 生成中
 * - confirmed: 已确认
 * - overdue: 已逾期
 */
export type PoolCycleStatus = 'generating' | 'confirmed' | 'overdue';

/**
 * 池周期记录
 */
export interface PoolCycle {
  id: string;
  poolId: string;
  monthKey: string;        // YYYY-MM
  totalAmount: number;
  dayCount: number;        // 实际天数
  dailyVirtual: number;   // 日均均摊金额
  /** v2.3:已认领（实际支付）总额 */
  paidAmount: number;
  status: PoolCycleStatus;
  transactions: PoolTransaction[];
}

/**
 * 池交易状态
 */
export type PoolTransactionStatus = 'virtual' | 'confirmed';

/**
 * 池交易记录
 */
export interface PoolTransaction {
  id: string;
  cycleId: string;
  dateKey: string;
  recordId?: string;      // 关联的 AccountRecord id
  amount: number;
  direction: 'in' | 'out';
  status: PoolTransactionStatus;
  confirmedAt?: number;
}

// ── Savings Goal ─────────────────────────────────────────────

/**
 * 存钱目标
 */
export interface SavingsGoal {
  id: string;
  name: string;
  targetAmount: number;
  currentAmount: number;
  targetAccountId?: string;
  deadline?: string;       // YYYY-MM-DD
  createdAt: number;
}

// ── Journey (一程) ──────────────────────────────────────────

/**
 * 一程（v3.0 TASK-059）
 *
 * 围绕一件事的记录集合：可以是支出程（旅行），也可以是收入程（兼职）。
 * 带封面图，在「我的」页以小红书式瀑布流陈列。
 */
export interface Journey {
  id: string;
  /** 标题，≤ 20 字 */
  title: string;
  /** 支出程 / 收入程，与关联记录 type 必须一致 */
  type: RecordType;
  /** 封面图（IndexedDB imageId），无封面则用渐变占位卡 */
  coverImageId?: string;
  /** 备注 */
  note?: string;
  /** 起始日期（展示用，默认取关联记录 min dateKey） */
  startKey?: string;        // YYYY-MM-DD
  /** 结束日期（展示用，默认取关联记录 max dateKey） */
  endKey?: string;          // YYYY-MM-DD
  createdAt: number;
  updatedAt: number;
}

// ── Accounting Store State ───────────────────────────────────

/**
 * 记账 Store 状态
 */
export interface AccountingState {
  // 账户
  accounts: Account[];
  // 分类（收入 + 支出）
  categories: Category[];
  // 分类文件夹
  folders: Folder[];
  // 记账记录
  records: AccountRecord[];
  // 池配置
  pools: PoolConfig[];
  // 池周期
  cycles: PoolCycle[];
  // 存钱目标
  savingsGoals: SavingsGoal[];
}
